import { useTranslation } from "react-i18next";

import { ChallengePanel } from "./ChallengePanel.js";
import { Powers } from "../../components/game/Powers.js";
import { OutcomeFlash } from "../../components/game/OutcomeFlash.js";
import { SideBoard } from "../../components/game/SideBoard.js";
import { StallNotice } from "../../components/game/StallNotice.js";
import {
  challengedSlotsFor,
  claimedSlotFor,
  guessTextFor,
  wonByChallenge,
} from "../../lib/placement.js";
import { sideLabel } from "../../lib/roster.js";
import {
  challenge,
  guess,
  place,
  purchase,
  revote,
  stallVote,
  steal,
  type GameLink,
  type GameView,
} from "../../services/gameLink.js";
import { FlagPanel } from "./FlagPanel.js";
import { GuessPanel } from "./GuessPanel.js";
import { PlacePanel } from "./PlacePanel.js";
import { RateCard } from "./RateCard.js";
import { StageBody } from "./StageBody.js";
import { StealPanel } from "./StealPanel.js";
import type { Step } from "./step.js";

/**
 * The one thing on screen — phase 18, finding #23.
 *
 * # What a stage is
 *
 * `step.ts` decides *which* question is being asked; this renders it, and
 * nothing else. The shell around it ([`RoundPanel`]) owns the chrome that never
 * moves — the round, the clock, the coins, the single primary action — so this
 * file never has to think about what is above or below it.
 *
 * # The spectator half is the point, not a fallback
 *
 * The request was *"make it all about the active player — other players see the
 * active player's timeline, the current song, and the selections they are
 * making, so everyone knows the current state"*. In Classic that is most of the
 * room for most of the round, so the watching stages are not a lesser version of
 * the playing ones: they are what most people are looking at most of the time,
 * and they are where findings #1 and #9 actually land.
 *
 * ⚠️ **Everything here comes from `components/game/`.** ADR 0005's rule is that
 * the display never imports the player shell, and the cheapest way to break it
 * is to build a board here that the television then reaches for.
 *
 * ⚠️ **Changing which sides a case draws means changing
 * [`sidesDrawnByStage`](./step.js) with it.** That function is what stops the
 * extras column from drawing a board twice — or, worse, from omitting one this
 * file never drew. It sits in `step.ts` because it is a fact about the step,
 * and it is covered by two tests in `RoundPanel.test.tsx` for the day somebody
 * changes one without the other.
 */
export function RoundStage({
  step,
  view,
  link,
  busy,
  aiming,
  onAim,
  send,
  sendTracked,
}: {
  step: Step;
  view: GameView;
  link: GameLink;
  busy: boolean;
  /** Which targeted power the player has open, if any. */
  aiming: "challenge" | "steal" | null;
  onAim: (item: "challenge" | "steal" | null) => void;
  /** Fire and forget; the shell shows any refusal. */
  send: (action: () => Promise<unknown>) => void;
  /** Same, but the caller also learns it failed — see finding #2. */
  sendTracked: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const board = view.board;
  const you = view.you;
  if (!board) {
    return null;
  }
  const round = board.round ?? null;
  const answer = round?.answer ?? null;
  const activeSide = board.sides.find((side) => side.id === board.active_side) ?? null;
  /** What the active side said it was — `null` until the grids close. */
  const activeClaim = guessTextFor(board, board.active_side);

  /**
   * What this player may buy at this moment, and the picker for the one that
   * needs a target.
   *
   * Built once and handed to every stage: the engine's list is already
   * phase-correct, so *where* the strip appears is not a decision this file has
   * to keep making. ⚠️ `StealPanel` renders beside it rather than inside it —
   * it is a target picker over the whole board, not a control in a row.
   */
  const powers = you && (
    <>
      <Powers
        rules={view.rules}
        coins={you.coins}
        availableNow={you.available_purchases}
        busy={busy}
        onBuy={(item) => {
          send(() => purchase(link, item));
        }}
        onAim={(item) => {
          onAim(item);
        }}
      />
      {aiming === "steal" && (
        <StealPanel
          board={board}
          roster={view.roster}
          yourSide={you.side}
          price={view.rules.steal}
          coins={you.coins}
          stealsUsed={you.steals_used}
          stealsAllowed={you.steals_allowed}
          busy={busy}
          open
          onOpenChange={(open) => {
            onAim(open ? "steal" : null);
          }}
          onSteal={(from, track) => {
            send(() => steal(link, from, track));
            onAim(null);
          }}
        />
      )}
    </>
  );
  const mySide = board.sides.find((side) => side.id === you?.side) ?? null;

  /** The active side's board, which is what the room is watching. */
  const theirBoard = activeSide && (
    <SideBoard
      side={activeSide}
      label={sideLabel(view.roster, activeSide.id)}
      active
      yours={activeSide.id === you?.side}
      cardsToWin={view.rules.cards_to_win}
      claimedSlot={claimedSlotFor(board, activeSide.id)}
      takenSlots={challengedSlotsFor(board, activeSide.id, view.roster)}
      guess={guessTextFor(board, activeSide.id)}
      wonByChallenge={wonByChallenge(board, activeSide.id)}
      outcome={board.round?.outcomes.find((o) => o.side === activeSide.id) ?? null}
    />
  );

  switch (step) {
    case "deal":
    case "waitDeal":
      // Nothing is in flight, so the stage is the board itself — the one moment
      // a player is deciding whether to spend before a card exists, which is
      // when `blind_timeline` is on sale and why it was unfindable (#21).
      return (
        <StageBody title={t("round.phase.turn_ready")} view={view} powers={powers}>
          {theirBoard}
        </StageBody>
      );

    case "listen":
      // ⚠️ **The vinyl is the whole stage.** There is nothing to decide, and a
      // screen that offered options anyway would be inviting a tap the engine
      // refuses — which is what "not your turn" greyed controls read as.
      return (
        <StageBody title={t("round.phase.listening")} view={view} powers={powers}>
          {theirBoard}
        </StageBody>
      );

    case "answer":
    case "answered":
      return (
        <StageBody title={t("round.phase.guessing")} view={view} powers={powers}>
          {round && (
            <GuessPanel
              // Keyed by the round: the panel holds an optimistic selection
              // across one round trip, and a tap from last round is not an
              // answer to this card (finding #2).
              key={board.round_no}
              round={round}
              you={you ?? null}
              answer={answer}
              live={step === "answer"}
              locked={you?.guess_locked ?? false}
              onGuess={(artistOption, titleOption) =>
                sendTracked(() => guess(link, artistOption, titleOption))
              }
            />
          )}
        </StageBody>
      );

    case "place":
      // Full width, and the only thing on screen: finding #16 measured the
      // claimed slot at a third the width of a card, on a screen that also
      // carried four side panels above it.
      return (
        <StageBody title={t("round.phase.placing")} view={view} powers={powers}>
          <PlacePanel
            key={board.round_no}
            side={mySide}
            cardsToWin={view.rules.cards_to_win}
            live
            showTimeline
            busy={busy}
            onPlace={(chosen, year) => {
              send(() => place(link, chosen, year));
            }}
          />
        </StageBody>
      );

    case "watchPlace":
      return (
        <StageBody title={t("round.phase.placing")} view={view} powers={powers}>
          {theirBoard}
        </StageBody>
      );

    case "revote": {
      // 🔴 **The team voted and nothing won** — phase 20h-3, ADR 0029. Without
      // this stage the round simply looks stuck: a placing window that will not
      // close, a clock running down, and nothing on screen saying why. The
      // shape of the tie is the explanation, so the votes are drawn rather than
      // summarised.
      const votes = you?.team_votes ?? [];
      const left = you?.revotes_left ?? 0;
      const price = you?.revote_price ?? 0;
      const canPay = (you?.coins ?? 0) >= price;
      // ⚠️ **A tally, not a list of who voted for what.** The roster carries
      // member *names* and `team_votes` carries player *ids*, with no mapping
      // between them — so naming anybody here would be a guess. It is also the
      // better answer: what makes a tie legible is its shape ("two of us said
      // before, two said after"), and who said which is the team's own business,
      // for the reason `Notice::Revoted` carries no slot.
      const tally = new Map<number, number>();
      for (const vote of votes) {
        if (typeof vote.slot === "number") {
          tally.set(vote.slot, (tally.get(vote.slot) ?? 0) + 1);
        }
      }
      return (
        <StageBody title={t("round.revote.title")} view={view} powers={powers}>
          <p className="text-sm text-white/70">{t("round.revote.lead")}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {[...tally.entries()]
              .sort(([a], [b]) => a - b)
              .map(([slot, count]) => (
                <li key={slot} className="flex justify-between gap-3">
                  <span className="text-white/80">
                    {t("round.revote.gap", { slot: slot + 1 })}
                  </span>
                  <span className="tabular-nums text-white/55">
                    {t("round.revote.votes", { count })}
                  </span>
                </li>
              ))}
          </ul>
          {left > 0 ? (
            <button
              type="button"
              disabled={busy || !canPay}
              onClick={() => {
                send(() => revote(link));
              }}
              className="min-h-14 rounded-lg bg-flash-500 px-4 text-lg font-semibold text-stage-950 disabled:opacity-40"
            >
              {canPay
                ? t("round.revote.buy", { price, left })
                : t("round.revote.cannotAfford", { price })}
            </button>
          ) : (
            // The cap is reached and the card is going. Said plainly rather
            // than as a disabled button with no explanation.
            <p role="status" className="text-sm text-wrong-500">
              {t("round.revote.spent")}
            </p>
          )}
        </StageBody>
      );
    }

    case "challenge":
      // A challenge window with no round is not a state the engine produces;
      // narrowing it here keeps the panel's props honest rather than widening
      // them to a `null` it would have to handle in six places.
      if (!round) {
        return null;
      }
      // **The move everybody else gets** (phase 14 C1), and since 16a a move you
      // can actually aim: the claim is drawn on the timeline you are contesting.
      return (
        <StageBody title={t("round.challenge.title")} view={view} powers={powers}>
          {/* 🔴 **A challenge carries an answer since 20h-1** (ADR 0029) — the
              same grids the active player had, plus a year. The slot is still
              the only part that wins the card, which is what keeps the engine
              tiebreak-free; the rest pays coins, and only if the slot was right.
              Phase 18's finding #1 lives on inside the panel: a challenger sees
              *what* the active side said it was, not only where they put it. */}
          <ChallengePanel
            round={round}
            activeSide={activeSide}
            activeLabel={sideLabel(view.roster, board.active_side)?.name ?? null}
            claim={activeClaim}
            claimedSlot={claimedSlotFor(board, board.active_side)}
            takenSlots={challengedSlotsFor(board, board.active_side, view.roster)}
            you={you}
            price={view.rules.challenge}
            busy={busy}
            onChallenge={(chosen, artistOption, titleOption, exactYear) => {
              send(() =>
                challenge(link, chosen, { artistOption, titleOption, exactYear }),
              );
            }}
          />
        </StageBody>
      );

    case "awaitChallenge":
      // **Finding #9, and it is the mirror of the one that opened phase 16.**
      // 16a made the placement public so a challenger could aim; the claims
      // *against* the active side were never shown to them, so the person whose
      // card was at stake was the only one who could not see the contest.
      return (
        <StageBody title={t("round.phase.challenge")} view={view} powers={powers}>
          {theirBoard}
          {you && you.side !== board.active_side ? (
            // A challenger who has had their say. **Which** of the two answers
            // they gave, so a phone that reconnects mid-window does not look
            // like it lost the tap.
            <p role="status" className="text-xs text-white/55">
              {you.challenge_answered && round?.challenged_slots.length
                ? t("round.challenge.claimed")
                : t("round.challenge.passed")}
            </p>
          ) : (
            // The active side, who until phase 18 could see none of this.
            <p role="status" className="text-xs text-white/55">
              {round?.challenged_slots.length
                ? t("round.challenge.against", { count: round.challenged_slots.length })
                : t("round.challenge.noneYet")}
            </p>
          )}
        </StageBody>
      );

    case "reveal":
    case "advanceRound": {
      // **Finding #8: winning a card has to *happen*.** Everything about it was
      // typographic — a word in green on an option, a line of status text — and
      // status describes a state rather than marking a moment. Keyed by the
      // round so it is a new element each time rather than a restarted
      // animation, and only for the viewer's own side: a celebration for
      // somebody else's card is noise.
      const mine = board.round?.outcomes.find((outcome) => outcome.side === you?.side);
      const flash = mine
        ? mine.wins_card || wonByChallenge(board, you?.side ?? "")
          ? ("won" as const)
          : ("lost" as const)
        : null;
      return (
        <StageBody title={t("round.phase.reveal")} view={view} powers={powers}>
          {flash && <OutcomeFlash key={board.round_no} flash={flash} />}
          {answer && (
            <section
              data-testid="answer-card"
              className="rounded-xl border-2 border-flash-500/50 bg-flash-500/10 p-4 text-center"
            >
              {/* ⚠️ Two different absences, and they must not read the same:
                  the whole answer is absent before `Reveal` (the `answer &&`
                  above), while a null year *inside* a present answer means an
                  undated round — phase 19g — which never had one. */}
              <p className="text-5xl font-semibold tabular-nums text-flash-400">
                {answer.year ?? t("round.undatedName")}
              </p>
              <p className="mt-1 text-base font-medium">{answer.title}</p>
              <p className="text-sm text-white/60">{answer.artist}</p>
            </section>
          )}
          {/* 🔴 **The round has one more step than the screen used to draw** —
              ADR 0026, findings 5.1 and 5.2.

              5.2 is the strongest instance of the junk-drawer problem the ADR
              is about: the engine **enters a dedicated phase** for the
              year-is-wrong question and **runs a countdown for it**, and the
              screen rendered no control for the action the phase exists to
              collect. A player was being asked a question by a timer.

              ⚠️ **Both prompts span `reveal` → `dispute` → `settled`**, which is
              the whole window the engine accepts them in. Showing them only in
              `Dispute` would make a prompt that vanishes when the phase ticks —
              the same defect in a smaller form. Each keeps its own gate: the
              flag knows whether you already raised one, the rating knows
              whether you already answered.

              📌 The flag sits above the rating, as it did in the drawer, and
              for the reason recorded there: the flag is the claim that can void
              the round, the rating is the quieter one about the card. */}
          {round && (board.phase === "reveal" || board.phase === "dispute") && (
            <FlagPanel link={link} phase={board.phase} round={round} you={you ?? null} />
          )}
          {round && <RateCard link={link} />}

          {theirBoard}
        </StageBody>
      );
    }

    case "stallWaiting":
    case "stallVote": {
      // 🔴 **Phase 21, ADR 0030.** A stalled party looks exactly like a working
      // one nobody is touching, and this is the only thing that says otherwise.
      // No board under it: the room is not looking at anybody's cards, it is
      // deciding whether there is still a game.
      const stall = board.stall;
      const mine = stall?.votes.find((vote) => vote.player === you?.player);
      return (
        <StageBody title={t("stall.title")} view={view} powers={powers}>
          {stall && (
            <StallNotice
              stall={stall}
              yourVote={mine?.close ?? null}
              // Read-only for a spectator: `you` is the seat, and somebody with
              // no seat has no answer to give.
              onVote={
                you
                  ? (close) => {
                      send(() => stallVote(link, close));
                    }
                  : undefined
              }
            />
          )}
        </StageBody>
      );
    }

    case "defend":
    case "stealPending": {
      // **Finding #24, and the only stage that is about somebody else's move.**
      // The thief has paid and the room has been told; the card has not moved
      // and will not until this closes. ⚠️ Absent counts as declined, so a phone
      // that never answers reaches the same outcome as tapping *let them*.
      const pending = board.pending_steal;
      const thief = pending ? sideLabel(view.roster, pending.by) : null;
      const victim = pending ? sideLabel(view.roster, pending.from) : null;
      const card = pending
        ? board.sides
            .find((s) => s.id === pending.from)
            ?.timeline.find((held) => held.track === pending.track)
        : undefined;
      return (
        <StageBody title={t("round.defend.title")} view={view} powers={powers}>
          <section
            data-testid="steal-window"
            className="rounded-xl border-2 border-wrong-500/60 bg-wrong-500/10 p-4"
          >
            <p className="text-base font-semibold">
              {step === "defend"
                ? t("round.defend.aimedAtYou", { who: thief?.name ?? "" })
                : t("round.defend.watching", {
                    who: thief?.name ?? "",
                    from: victim?.name ?? "",
                  })}
            </p>
            {card && (
              <p className="mt-1 text-sm text-white/70">
                {card.title} · {card.artist} · {card.year}
              </p>
            )}
            {step === "defend" && pending && (
              <p className="mt-2 text-sm text-white/60">
                {t("round.defend.cost", { price: pending.defence_price })}
              </p>
            )}
          </section>
          {theirBoard}
        </StageBody>
      );
    }

    case "finished":
      return (
        <StageBody title={t("round.phase.finished")} view={view} powers={powers}>
          {board.sides.map((side) => (
            <SideBoard
              key={side.id}
              side={side}
              label={sideLabel(view.roster, side.id)}
              active={side.id === board.winner}
              yours={side.id === you?.side}
              cardsToWin={view.rules.cards_to_win}
            />
          ))}
        </StageBody>
      );
  }
}
